import { Checkbox, Icon } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';

import type { ProduitsListeItemType } from '@/types/produit';
import { formatMonetaire } from '@/utils';

interface ProduitsListeTableRowProps {
  item: ProduitsListeItemType;
  rowProps: Record<string, unknown>;
  /** Affiche une ligne avec moins d'informations */
  basic?: boolean;
  /** Régie autonome (produits au comptant) */
  autonome?: boolean;
  onDetailClick?: (item: ProduitsListeItemType) => void;
}

const ProduitsListeTableRow = (props: ProduitsListeTableRowProps) => {
  const { basic, autonome, item, rowProps, onDetailClick } = props;

  const {
    identifiant,
    libelle,
    source,
    prixUnitaireTTC,
    codeProduitLocal,
    article,
    tva,
    payableCESU,
    payableChequeVacance,
  } = item;

  // La modale de détail ne s'ouvre que dans la vue Paramètres (non-basic).
  const detailProps = !basic && onDetailClick ? { onDoubleClick: () => onDetailClick(item) } : {};

  // Produits encaissables au comptant : colonnes du ticket (prix TTC, TVA, CESU, chèque vacances).
  if (autonome) {
    return (
      <ListeTable.Row key={identifiant} {...rowProps}>
        <ListeTable.Cell name="libelle" {...detailProps}>
          {libelle}
        </ListeTable.Cell>
        <ListeTable.Cell name="prixUnitaireTTC" textAlign="right" {...detailProps}>
          {formatMonetaire(prixUnitaireTTC, 2, true)}
        </ListeTable.Cell>
        <ListeTable.Cell name="tva" textAlign="right" {...detailProps}>
          {tva > 0 ? `${tva}%` : '-'}
        </ListeTable.Cell>
        <ListeTable.Cell name="article" {...detailProps}>
          {article}
        </ListeTable.Cell>
        <ListeTable.Cell name="codeProduitLocal" {...detailProps}>
          {codeProduitLocal}
        </ListeTable.Cell>
        <ListeTable.Cell name="payableCesu" textAlign="center" {...detailProps}>
          <Checkbox checked={payableCESU} readOnly style={{ pointerEvents: 'none' }} />
        </ListeTable.Cell>
        <ListeTable.Cell name="payableChequeVacances" textAlign="center" {...detailProps}>
          <Checkbox checked={payableChequeVacance} readOnly style={{ pointerEvents: 'none' }} />
          {!basic && (
            <Icon
              data-testid="button-eye"
              className="liste-table-row-icon"
              name="eye"
              style={{ marginLeft: 10 }}
              fitted
              onClick={() => onDetailClick?.(item)}
              link
              title=""
            />
          )}
        </ListeTable.Cell>
      </ListeTable.Row>
    );
  }

  // Produits encaissés sur facture — vue Constitution (comportement d'origine).
  if (basic) {
    return (
      <ListeTable.Row key={identifiant} {...rowProps}>
        <ListeTable.Cell name="libelle">{libelle}</ListeTable.Cell>
        <ListeTable.Cell name="article">{article}</ListeTable.Cell>
        <ListeTable.Cell name="codeProduitLocal">{codeProduitLocal}</ListeTable.Cell>
        <ListeTable.Cell name="tva" textAlign="right">
          {tva > 0 ? `${tva}%` : '-'}
        </ListeTable.Cell>
        <ListeTable.Cell name="source" textAlign="center">
          {source}
        </ListeTable.Cell>
      </ListeTable.Row>
    );
  }

  // Produits encaissés sur facture — vue Paramètres (comportement d'origine).
  return (
    <ListeTable.Row key={identifiant} {...rowProps}>
      <ListeTable.Cell name="libelle" {...detailProps}>
        {libelle}
      </ListeTable.Cell>
      <ListeTable.Cell name="article" {...detailProps}>
        {article}
      </ListeTable.Cell>
      <ListeTable.Cell name="codeProduitLocal" {...detailProps}>
        {codeProduitLocal}
      </ListeTable.Cell>
      <ListeTable.Cell name="tva" textAlign="right" {...detailProps}>
        {tva > 0 ? `${tva}%` : '-'}
      </ListeTable.Cell>
      <ListeTable.Cell name="payableCesu" textAlign="center" {...detailProps}>
        <Checkbox checked={payableCESU} readOnly style={{ pointerEvents: 'none' }} />
      </ListeTable.Cell>
      <ListeTable.Cell name="payableChequeVacances" textAlign="center" {...detailProps}>
        <Checkbox checked={payableChequeVacance} readOnly style={{ pointerEvents: 'none' }} />
      </ListeTable.Cell>
      <ListeTable.Cell name="source" textAlign="center" {...detailProps}>
        <div style={{ display: 'flex' }}>
          <div className="textEllipsis" style={{ paddingRight: 10, flex: 1 }}>
            {source}
          </div>
          <Icon
            data-testid="button-eye"
            className="liste-table-row-icon"
            name="eye"
            style={{ float: 'right' }}
            fitted
            onClick={() => onDetailClick?.(item)}
            link
            title=""
          />
        </div>
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default ProduitsListeTableRow;
